import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import useClientStore from '@/stores/useClientStore';
import useDossierStore from '@/stores/useDossierStore';
import { expertisesApi } from '@/api';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import EntrepriseSearch from '@/components/ui/EntrepriseSearch';
import { FORME_JURIDIQUE_OPTIONS } from '@/constants/enums';

const STEPS = [
  { number: 1, title: 'Client' },
  { number: 2, title: 'Dossier' },
  { number: 3, title: 'Type d\'expertise' },
];

const USAGE_OPTIONS = [
  { value: 'bancaire', label: 'Bancaire' },
  { value: 'successorale', label: 'Successorale' },
  { value: 'fiscale', label: 'Fiscale' },
  { value: 'patrimoniale', label: 'Patrimoniale' },
  { value: 'judiciaire', label: 'Judiciaire' },
];

const CATEGORIE_OPTIONS = [
  { value: 'propriete', label: 'Propriété' },
  { value: 'lot', label: 'Lot en copropriété' },
];

const FORMAT_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'bcg', label: 'Bilan Comptable Général (BCG)' },
];

const TYPE_VALEUR_OPTIONS = [
  { value: 'venale', label: 'Valeur vénale' },
  { value: 'locative', label: 'Valeur locative' },
  { value: 'venale_et_locative', label: 'Valeur vénale et locative' },
];

const OCCUPATION_OPTIONS = [
  { value: 'libre', label: 'Libre' },
  { value: 'occupee', label: 'Occupée' },
];

export default function DossierCreatePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { clients, fetchClients, createClient } = useClientStore();
  const { createDossier } = useDossierStore();

  const [formData, setFormData] = useState({
    // Step 1: Client
    clientType: 'existing',
    selectedClient: '',
    // Nouveau client — type
    newClientType: 'particulier',
    // Nouveau client — particulier
    newClientCivilite: 'M.',
    newClientNom: '',
    newClientPrenom: '',
    newClientEmail: '',
    newClientTelephone: '',
    // Nouveau client — entreprise
    newClientRaisonSociale: '',
    newClientSiret: '',
    newClientFormeJuridique: '',
    newClientRepresentantNom: '',
    newClientRepresentantPrenom: '',
    // Step 2: Dossier
    dateMandat: '',
    usageRapport: '',
    // Step 3: Expertise
    categorie: 'propriete',
    format: 'standard',
    typeValeur: 'venale',
    occupation: 'libre',
    dateVisite: '',
  });

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEntrepriseSelect = (entreprise) => {
    setFormData((prev) => ({
      ...prev,
      newClientRaisonSociale: entreprise.nom_complet || prev.newClientRaisonSociale,
      newClientSiret: entreprise.siret || prev.newClientSiret,
      newClientRepresentantPrenom: entreprise.dirigeant
        ? entreprise.dirigeant.split(' ')[0]
        : prev.newClientRepresentantPrenom,
      newClientRepresentantNom: entreprise.dirigeant
        ? entreprise.dirigeant.split(' ').slice(1).join(' ')
        : prev.newClientRepresentantNom,
    }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        if (formData.clientType === 'existing') {
          return formData.selectedClient !== '';
        }
        if (formData.newClientType === 'particulier') {
          return formData.newClientNom !== '' && formData.newClientPrenom !== '';
        }
        // entreprise
        return formData.newClientNom !== '' && formData.newClientRaisonSociale !== '';
      case 2:
        return (
          formData.dateMandat !== '' &&
          formData.usageRapport !== ''
        );
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let clientId = formData.selectedClient;

      // Step 1: Create client if needed
      if (formData.clientType === 'new') {
        const isEntreprise = formData.newClientType === 'entreprise';
        const clientPayload = {
          type: formData.newClientType,
          civilite: isEntreprise ? 'Société' : formData.newClientCivilite,
          nom: formData.newClientNom,
          prenom: isEntreprise ? undefined : (formData.newClientPrenom || undefined),
          raison_sociale: isEntreprise ? formData.newClientRaisonSociale : undefined,
          siret: isEntreprise ? (formData.newClientSiret || undefined) : undefined,
          forme_juridique: isEntreprise ? (formData.newClientFormeJuridique || undefined) : undefined,
          representant_nom: isEntreprise ? (formData.newClientRepresentantNom || undefined) : undefined,
          representant_prenom: isEntreprise ? (formData.newClientRepresentantPrenom || undefined) : undefined,
          email: formData.newClientEmail || undefined,
          telephone: formData.newClientTelephone || undefined,
        };
        const newClient = await createClient(clientPayload);
        clientId = newClient.id;
      }

      // Step 2: Create dossier
      const newDossier = await createDossier({
        statut: 'brouillon',
        date_mandat: formData.dateMandat,
        usage_rapport: formData.usageRapport,
        client_id: clientId,
      });

      // Step 3: Create expertise
      await expertisesApi.create({
        dossier_id: newDossier.id,
        categorie_expertise: formData.categorie,
        format_rapport: formData.format,
        type_valeur: formData.typeValeur,
        occupation: formData.occupation,
        date_visite: formData.dateVisite || null,
        date_rapport: null,
        date_valorisation: null,
      });

      // Navigate to dossier detail
      navigate(`/dossiers/${newDossier.id}`);
    } catch (error) {
      setSubmitError(error.message || 'Une erreur est survenue');
      setIsSubmitting(false);
    }
  };

  const clientOptions = clients.map((c) => ({
    value: c.id,
    label: c.type === 'entreprise'
      ? (c.raison_sociale || c.nom)
      : `${c.prenom || ''} ${c.nom}`.trim(),
  }));

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Créer un nouveau dossier</h1>
        <p className="text-slate-600 mt-1">
          Étape {currentStep} sur {STEPS.length}
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => (
          <div key={step.number} className="flex items-center flex-1">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold transition-all ${
                currentStep >= step.number
                  ? 'bg-carex-600 border-carex-600 text-white'
                  : 'border-slate-300 text-slate-400'
              }`}
            >
              {step.number}
            </div>
            <div className="ml-3">
              <p
                className={`text-sm font-medium ${
                  currentStep >= step.number ? 'text-slate-900' : 'text-slate-500'
                }`}
              >
                {step.title}
              </p>
            </div>
            {index < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-4 transition-all ${
                  currentStep > step.number ? 'bg-carex-600' : 'bg-slate-300'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{submitError}</p>
        </div>
      )}

      {/* Form Content */}
      <Card className="p-6 space-y-6">
        {/* Step 1: Client */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Sélectionner ou créer un client
            </h2>

            {/* Client Type Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Mode de sélection</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="clientType"
                    value="existing"
                    checked={formData.clientType === 'existing'}
                    onChange={(e) => handleInputChange('clientType', e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-slate-700">Client existant</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="clientType"
                    value="new"
                    checked={formData.clientType === 'new'}
                    onChange={(e) => handleInputChange('clientType', e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-slate-700">Créer un nouveau client</span>
                </label>
              </div>
            </div>

            {/* Existing Client Selection */}
            {formData.clientType === 'existing' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Sélectionner un client
                </label>
                <Select
                  name="selectedClient"
                  options={clientOptions}
                  value={formData.selectedClient}
                  onChange={(e) => handleInputChange('selectedClient', e.target.value)}
                />
              </div>
            )}

            {/* New Client Form */}
            {formData.clientType === 'new' && (
              <div className="space-y-4 bg-carex-50 p-4 rounded-lg">
                {/* Type toggle */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleInputChange('newClientType', 'particulier')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                      formData.newClientType === 'particulier'
                        ? 'bg-carex-600 text-white'
                        : 'bg-white text-slate-700 border border-slate-300'
                    }`}
                  >
                    Particulier
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInputChange('newClientType', 'entreprise')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                      formData.newClientType === 'entreprise'
                        ? 'bg-carex-600 text-white'
                        : 'bg-white text-slate-700 border border-slate-300'
                    }`}
                  >
                    Entreprise
                  </button>
                </div>

                {/* Particulier fields */}
                {formData.newClientType === 'particulier' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-slate-700">Civilité</label>
                        <Select
                          name="newClientCivilite"
                          options={[
                            { value: 'M.', label: 'Monsieur' },
                            { value: 'Mme', label: 'Madame' },
                          ]}
                          value={formData.newClientCivilite}
                          onChange={(e) => handleInputChange('newClientCivilite', e.target.value)}
                        />
                      </div>
                      <div />
                    </div>
                    <Input
                      label="Nom"
                      name="newClientNom"
                      placeholder="Dupont"
                      value={formData.newClientNom}
                      onChange={(e) => handleInputChange('newClientNom', e.target.value)}
                    />
                    <Input
                      label="Prénom"
                      name="newClientPrenom"
                      placeholder="Jean"
                      value={formData.newClientPrenom}
                      onChange={(e) => handleInputChange('newClientPrenom', e.target.value)}
                    />
                  </>
                )}

                {/* Entreprise fields */}
                {formData.newClientType === 'entreprise' && (
                  <>
                    <EntrepriseSearch
                      label="Rechercher une entreprise"
                      name="wizardEntrepriseSearch"
                      onSelect={handleEntrepriseSelect}
                    />
                    <Input
                      label="Nom (alias court)"
                      name="newClientNom"
                      placeholder="Dupont Immo"
                      value={formData.newClientNom}
                      onChange={(e) => handleInputChange('newClientNom', e.target.value)}
                    />
                    <Input
                      label="Raison sociale"
                      name="newClientRaisonSociale"
                      placeholder="SCI Dupont Immobilier"
                      value={formData.newClientRaisonSociale}
                      onChange={(e) => handleInputChange('newClientRaisonSociale', e.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="SIRET"
                        name="newClientSiret"
                        placeholder="12345678901234"
                        value={formData.newClientSiret}
                        onChange={(e) => handleInputChange('newClientSiret', e.target.value)}
                      />
                      <Select
                        label="Forme juridique"
                        name="newClientFormeJuridique"
                        options={[{ value: '', label: '—' }, ...FORME_JURIDIQUE_OPTIONS]}
                        value={formData.newClientFormeJuridique}
                        onChange={(e) => handleInputChange('newClientFormeJuridique', e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Représentant (prénom)"
                        name="newClientRepresentantPrenom"
                        placeholder="Jean"
                        value={formData.newClientRepresentantPrenom}
                        onChange={(e) => handleInputChange('newClientRepresentantPrenom', e.target.value)}
                      />
                      <Input
                        label="Représentant (nom)"
                        name="newClientRepresentantNom"
                        placeholder="Dupont"
                        value={formData.newClientRepresentantNom}
                        onChange={(e) => handleInputChange('newClientRepresentantNom', e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* Common fields */}
                <Input
                  label="Email (optionnel)"
                  name="newClientEmail"
                  type="email"
                  placeholder="contact@example.com"
                  value={formData.newClientEmail}
                  onChange={(e) => handleInputChange('newClientEmail', e.target.value)}
                />
                <Input
                  label="Téléphone (optionnel)"
                  name="newClientTelephone"
                  placeholder="06 12 34 56 78"
                  value={formData.newClientTelephone}
                  onChange={(e) => handleInputChange('newClientTelephone', e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 2: Dossier */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Informations du dossier</h2>
            <p className="text-sm text-slate-500">
              La référence du dossier sera générée automatiquement (EXP-{new Date().getFullYear()}-XXXXX).
            </p>
            <Input
              label="Date du mandat"
              type="date"
              value={formData.dateMandat}
              onChange={(e) => handleInputChange('dateMandat', e.target.value)}
              required
            />
            <Select
              label="Usage du rapport"
              name="usageRapport"
              options={USAGE_OPTIONS}
              value={formData.usageRapport}
              onChange={(e) => handleInputChange('usageRapport', e.target.value)}
            />
          </div>
        )}

        {/* Step 3: Type d'expertise */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">Type d'expertise</h2>
            <Select
              label="Catégorie d'expertise"
              name="categorie"
              options={CATEGORIE_OPTIONS}
              value={formData.categorie}
              onChange={(e) => handleInputChange('categorie', e.target.value)}
            />
            <Select
              label="Format du rapport"
              name="format"
              options={FORMAT_OPTIONS}
              value={formData.format}
              onChange={(e) => handleInputChange('format', e.target.value)}
            />
            <Select
              label="Type de valeur"
              name="typeValeur"
              options={TYPE_VALEUR_OPTIONS}
              value={formData.typeValeur}
              onChange={(e) => handleInputChange('typeValeur', e.target.value)}
            />
            <Select
              label="Occupation"
              name="occupation"
              options={OCCUPATION_OPTIONS}
              value={formData.occupation}
              onChange={(e) => handleInputChange('occupation', e.target.value)}
            />
            <Input
              label="Date de visite"
              type="date"
              value={formData.dateVisite}
              onChange={(e) => handleInputChange('dateVisite', e.target.value)}
            />
          </div>
        )}
      </Card>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          onClick={handleBack}
          disabled={currentStep === 1}
          className="disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
          Précédent
        </Button>

        <Button
          variant="primary"
          onClick={handleNext}
          disabled={!isStepValid() || isSubmitting}
          className="disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <span className="mr-2">Création en cours...</span>
            </>
          ) : currentStep === STEPS.length ? (
            'Créer le dossier'
          ) : (
            <>
              Suivant
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
